import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Medal, Crown, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface LeaderboardEntry {
  game_id: string;
  user_id: string;
  username: string | null;
  avatar_url: string | null;
  total_score: number;
  wins: number;
  games_played: number;
  best_score: number;
  rank: number;
}

const GAMES = [
  { id: 'loup-garou', name: 'Loup-Garou', emoji: '🐺', color: 'text-wolf' },
  { id: 'impro', name: 'Impro Théâtrale', emoji: '🎭', color: 'text-seer' },
  { id: 'tribunal', name: 'Le Tribunal', emoji: '⚖️', color: 'text-amber-400' },
  { id: 'appel-absurde', name: 'Appel Absurde', emoji: '📞', color: 'text-emerald-400' },
  { id: 'murder-mystery', name: 'Murder Mystery', emoji: '🔍', color: 'text-red-400' },
  { id: 'rpg-ia', name: 'Aventure RPG', emoji: '🗡️', color: 'text-violet-400' },
];

const Leaderboards = () => {
  const [selectedGame, setSelectedGame] = useState('loup-garou');
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedGame]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    
    const { data, error } = await supabase
      .from('leaderboards')
      .select('*')
      .eq('game_id', selectedGame)
      .order('rank', { ascending: true })
      .limit(50);
    
    if (!error && data) {
      setLeaderboard(data as LeaderboardEntry[]);
    }
    
    setLoading(false);
  };

  const currentGame = GAMES.find(g => g.id === selectedGame);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm text-muted-foreground">{rank}</span>;
  };

  return (
    <>
      <Helmet>
        <title>Classements - SuperSociety.ai</title>
        <meta name="description" content="Découvrez les meilleurs joueurs de SuperSociety.ai. Classements globaux pour chaque jeu." />
      </Helmet>

      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle, hsl(var(--moon) / 0.3) 0%, hsl(var(--accent) / 0.2) 30%, transparent 60%)',
            }}
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
          {/* Header */}
          <header className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Link>
            <Trophy className="w-8 h-8 text-moon" />
          </header>

          <div className="text-center mb-8">
            <h1 className="font-display text-4xl text-foreground mb-2">Classements</h1>
            <p className="text-muted-foreground">Les meilleurs joueurs de SuperSociety.ai</p>
          </div>

          {/* Game Selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {GAMES.map((game) => (
              <button
                key={game.id}
                onClick={() => setSelectedGame(game.id)}
                className={`
                  px-4 py-2 rounded-full border transition-all flex items-center gap-2
                  ${selectedGame === game.id 
                    ? `bg-accent/20 border-accent/50 ${game.color}` 
                    : 'bg-card/30 border-border/30 text-muted-foreground hover:border-border'}
                `}
              >
                <span>{game.emoji}</span>
                <span className="hidden sm:inline">{game.name}</span>
              </button>
            ))}
          </div>

          {/* Leaderboard */}
          <div className="p-6 rounded-2xl bg-card/50 border border-border/50 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl">{currentGame?.emoji}</span>
              <h2 className={`font-display text-2xl ${currentGame?.color}`}>
                {currentGame?.name}
              </h2>
            </div>

            {loading ? (
              <div className="text-center py-12 text-muted-foreground">
                Chargement...
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                Aucun joueur classé pour le moment.<br />
                Soyez le premier !
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((entry, index) => (
                  <div 
                    key={entry.user_id}
                    className={`
                      p-4 rounded-xl flex items-center gap-4 transition-all
                      ${entry.rank <= 3 
                        ? 'bg-accent/10 border border-accent/20' 
                        : 'bg-card/30 border border-border/20'}
                    `}
                  >
                    <div className="w-8 flex justify-center">
                      {getRankIcon(entry.rank)}
                    </div>
                    
                    <div className="w-10 h-10 rounded-full bg-background/50 border border-border/30 flex items-center justify-center overflow-hidden">
                      {entry.avatar_url ? (
                        <img src={entry.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="font-medium text-foreground">
                        {entry.username || 'Joueur anonyme'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {entry.games_played} parties • {entry.wins} victoires
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`font-bold text-lg ${currentGame?.color}`}>
                        {entry.total_score.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground">points</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Leaderboards;
